import React, { createContext, useCallback, useContext, useState } from "react";

import { cn } from "../lib/utils";

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue>({
  activeTab: "",
  setActiveTab: () => {},
});

/* ------------------------------------------------------------------ */
/* Tabs (root)                                                        */
/* ------------------------------------------------------------------ */

export function Tabs({
  className,
  defaultValue,
  value,
  onValueChange,
  orientation,
  children,
}: {
  className?: string;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  orientation?: string;
  children: React.ReactNode;
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? "");

  const isControlled = value !== undefined;
  const activeTab = isControlled ? value : uncontrolled;

  const setActiveTab = useCallback(
    (next: string) => {
      if (!isControlled) {
        setUncontrolled(next);
      }
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className} data-orientation={orientation}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* TabsList                                                            */
/* ------------------------------------------------------------------ */

export function TabsList({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "bg-muted text-muted-foreground inline-flex h-9 items-center justify-center rounded-lg p-1",
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* TabsTrigger                                                         */
/* ------------------------------------------------------------------ */

export function TabsTrigger({
  className,
  value,
  disabled,
  children,
}: {
  className?: string;
  value: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const { activeTab, setActiveTab } = useContext(TabsContext);
  const isActive = activeTab === value;

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isActive}
      disabled={disabled}
      className={cn(
        "ring-offset-background inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium whitespace-nowrap transition-all",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        isActive
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
        className,
      )}
      onClick={() => {
        if (!disabled) setActiveTab(value);
      }}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* TabsContent                                                         */
/* ------------------------------------------------------------------ */

export function TabsContent({
  className,
  value,
  children,
}: {
  className?: string;
  value: string;
  children: React.ReactNode;
}) {
  const { activeTab } = useContext(TabsContext);

  if (activeTab !== value) return null;

  return (
    <div
      role="tabpanel"
      className={cn(
        "ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        className,
      )}
    >
      {children}
    </div>
  );
}
